import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Popconfirm,
  Select,
  InputNumber,
  Drawer,
  Spin,
  message,
  Modal,
  List,
  Col,
  Row,
  Descriptions,
  Input,
} from "antd";
import FetchSelect from "Components/fetch-select";
import React, { useState, useEffect } from "react";
import { Controller, useWatch, useForm } from "react-hook-form";
import DatePicker from "Components/date-picker-localized";
import moment from "moment";
import { CURRENCY } from "Constants/";
import { RULES } from "Constants/rules";
import { yupResolver } from "@hookform/resolvers";
import {
  conditionValidationSchema,
  conditionsValidationSchema,
} from "ValidationModels/contracts";
import FormLayout, { FormItem } from "Components/form";
import { fixDates, clean } from "Utils/";
import { DISPLAY_MODE } from "Constants";
import { formatCurrency } from "Utils";
import { CONDITION_TIME_RATIO } from "Constants/contract";

export default function ContractConditionList({
  conditions,
  mode = DISPLAY_MODE.EDIT,
  options = {
    maxDateOfFinish: null,
    maxDateOfBeginning: null,
    contractType: null,
  },
  onAppend,
  onUpdate,
  onRemove,
  ...props
}) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [formVisibility, setFormVisibility] = useState(null);
  const [formOpened, setFormOpened] = useState(false);

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(conditionValidationSchema),
  });

  const {
    control,
    handleSubmit,
    errors,
    formState,
    reset,
    watch,
    setValue,
  } = form;

  const values = useWatch({
    control: control,
    name: ["purpose", "dateOfBeginning", "dateOfFinish"],
  });

  useEffect(() => {
    if (
      values.purpose?.rule?.code === RULES.RULE0007 //Rule Yapısı: RULE0007
    ) {
      setValue("dateOfFinish", null);
      setValue("frequencyVal", 1);
      setValue("frequencyUnit", { id: 1, description: "Ay", version: 0 });
    }
  }, [values.purpose]);

  const formOnSubmit = async (form) => {
    clean(form);

    form = {
      ...form,
      dateOfFinish:
        values.purpose?.rule?.code === RULES.RULE0007 //Rule Yapısı: RULE0007
          ? form.dateOfBeginning
          : form.dateOfFinish,
    };

    let oldConditions = [...conditions];
    if (selectedIndex !== null) oldConditions.splice(selectedIndex, 1);

    conditionsValidationSchema
      .validate([form, ...oldConditions])
      .then(async (value) => {
        if (selectedIndex !== null) {
          await onUpdate(selectedIndex, form);
        } else {
          await onAppend(form);
        }

        setFormVisibility(false);
      })
      .catch((error) => {
        message.error(error.message);
      });
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      message.error("Fiyat koşulu için girdiğiniz değerleri kontrol ediniz.");
  }, [errors]);

  return (
    <>
      <List
        dataSource={(conditions || []).sort((prev, current) => {
          if (prev.purpose.id < current.purpose.id) return -1;
          if (prev.purpose.id > current.purpose.id) return 1;
          return 0;
        })}
        style={{ backgroundColor: "#fff" }}
        bordered
        header={
          mode !== DISPLAY_MODE.READ_ONLY ? (
            <Button
              onClick={() => {
                reset({});
                setSelectedIndex(null);
                setFormVisibility(true);
              }}
            >
              Sözleşme Koşulu Ekle
            </Button>
          ) : null
        }
        renderItem={(item, index) => {
          return (
            <List.Item
              className="customized"
              key={item.id}
              actions={
                mode !== DISPLAY_MODE.READ_ONLY
                  ? [
                      <Button
                        onClick={() => {
                          reset(fixDates(item));
                          setSelectedIndex(index);
                          setFormVisibility(true);
                        }}
                      >
                        <FontAwesomeIcon icon={["fas", "edit"]} />
                      </Button>,

                      <Popconfirm
                        title="Emin Misin?"
                        onConfirm={() => onRemove(index)}
                        okText="Kaldır"
                        cancelText="Geri"
                      >
                        <Button type="danger">
                          <FontAwesomeIcon icon={["far", "trash-alt"]} />
                        </Button>
                      </Popconfirm>,
                    ]
                  : []
              }
            >
              <Descriptions column={{ xl: 4, lg: 4, md: 4, sm: 2, xs: 1 }}>
                <Descriptions.Item label="Amacı">
                  {item.purpose?.description}
                </Descriptions.Item>
                <Descriptions.Item label="Türü">
                  {item.type?.description}
                </Descriptions.Item>
                <Descriptions.Item label="Tarih" span={2}>
                  {moment(item.dateOfBeginning)
                    .startOf("day")
                    .isSame(moment(item.dateOfFinish))
                    ? moment(item.dateOfBeginning).format("DD.MM.YYYY")
                    : `${moment(item.dateOfBeginning).format(
                        "DD.MM.YYYY"
                      )}-${moment(item.dateOfFinish).format("DD.MM.YYYY")}`}
                </Descriptions.Item>
                <Descriptions.Item label="Zaman Oranı">
                  {item.timeRatio
                    ? Object.entries(CONDITION_TIME_RATIO).find(
                        (f) => f[1] === item.timeRatio
                      )[0]
                    : null}
                </Descriptions.Item>
                <Descriptions.Item label="Tutar">
                  {formatCurrency(item.documentAmount, item.documentCurrency)}
                </Descriptions.Item>
                <Descriptions.Item label="Sıklık">
                  {item.frequencyVal === 1 ? "" : item.frequencyVal}&nbsp;
                  {item.frequencyUnit?.description}da bir
                </Descriptions.Item>
                {item.maturityVal > 0 ? (
                  <Descriptions.Item label="Vade" span={2}>
                    {item.maturityVal} Gün
                  </Descriptions.Item>
                ) : null}
                {item.increaseType?.id ? (
                  <Descriptions.Item label="Artış Kuralı" span={4}>
                    {item.increaseType?.description}
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item label="Açıklama" span={4}>
                  {item.description || "-"}
                </Descriptions.Item>
              </Descriptions>
            </List.Item>
          );
        }}
      />
      <Drawer
        width={"50%"}
        placement="right"
        bodyStyle={{ overflowY: "auto", height: "100%", padding: 10 }}
        title={
          selectedIndex ? "Sözleşme Koşulu Güncelle" : "Sözleşme Koşulu Ekle"
        }
        visible={formVisibility}
        destroyOnClose={true}
        afterVisibleChange={(visible) => setFormOpened(visible)}
        onClose={() => {
          Modal.confirm({
            title: "Emin misiniz?",
            content: "Girdiğiniz koşul bilgileri kaybolacaktır.",
            okText: "Evet",
            cancelText: "İptal",
            onOk: () => setFormVisibility(false),
          });
        }}
      >
        {formOpened ? (
          <form onSubmit={handleSubmit(formOnSubmit)}>
            <FormLayout>
              <Col xs={24} sm={12}>
                <FormItem
                  label="Koşul Amacı"
                  required={true}
                  error={errors.purpose}
                >
                  <FetchSelect
                    api="/masterdata/conditionpurposes"
                    form={form}
                    field="purpose"
                    placeholder="Koşul Amacı seçiniz"
                  />
                </FormItem>
              </Col>
              <Col xs={24} sm={12}>
                <FormItem
                  label="Koşul Türü"
                  required={true}
                  error={errors.type}
                >
                  <FetchSelect
                    api="/masterdata/conditiontype"
                    filter={
                      options.contractType?.rule?.code === RULES.RULE0014 //Rule Yapısı: RULE0014
                        ? {
                            dataIndex: ["rule", "code"],
                            value: RULES.RULE0008, //Rule Yapısı: RULE0008
                          }
                        : null
                    }
                    form={form}
                    field="type"
                    placeholder="Koşul Türü seçiniz"
                  />
                </FormItem>
              </Col>
              <Col xs={24} sm={12}>
                <FormItem
                  label="Başlangıç Tarihi"
                  required={true}
                  error={errors.dateOfBeginning}
                >
                  <Controller
                    control={control}
                    name="dateOfBeginning"
                    as={
                      <DatePicker
                        format="DD.MM.YYYY"
                        disabledDate={(current) => {
                          return moment(current)
                            .startOf("d")
                            .isBefore(
                              moment(options.maxDateOfBeginning).startOf("d")
                            );
                        }}
                        style={{ width: "100%" }}
                      />
                    }
                  />
                </FormItem>
              </Col>
              <Col xs={24} sm={12}>
                <FormItem
                  label="Bitiş Tarihi"
                  required={true}
                  error={errors.dateOfFinish}
                >
                  <Controller
                    control={control}
                    name="dateOfFinish"
                    defaultValue={
                      options.maxDateOfFinish
                        ? moment(options.maxDateOfFinish)
                        : null
                    }
                    as={
                      <DatePicker
                        disabled={
                          values.purpose?.rule?.code === RULES.RULE0007 //Rule Yapısı: RULE0007
                        }
                        disabledDate={(current) => {
                          return (
                            (values.purpose?.rule?.code === RULES.RULE0007 &&
                              current <
                                (values.dateOfBeginning?.startOf("day") ||
                                  0)) ||
                            moment(current)
                              .startOf("d")
                              .isAfter(
                                moment(options.maxDateOfFinish).startOf("d")
                              )
                          );
                        }}
                        format="DD.MM.YYYY"
                        style={{ width: "100%" }}
                      />
                    }
                  />
                </FormItem>
              </Col>
              <Col xs={24}>
                <FormItem
                  label="Zaman Oranı"
                  required={true}
                  error={errors.timeRatio}
                >
                  <Controller
                    control={control}
                    name="timeRatio"
                    as={
                      <Select allowClear={true}>
                        {Object.entries(CONDITION_TIME_RATIO).map((c) => (
                          <Select.Option value={c[1]} key={c[1]}>
                            {c[0]}
                          </Select.Option>
                        ))}
                      </Select>
                    }
                  />
                </FormItem>
              </Col>

              <Col xs={24} sm={12}>
                <Row>
                  <Col xs={24} sm={12} lg={16}>
                    <FormItem
                      label="Tutar"
                      required={true}
                      error={errors.documentAmount}
                    >
                      <Controller
                        control={control}
                        name="documentAmount"
                        as={
                          <InputNumber
                            style={{ width: "100%" }}
                            min={0}
                            step={50}
                            decimalSeparator=","
                          />
                        }
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <FormItem
                      label="Para Birimi"
                      required={true}
                      error={errors.documentCurrency}
                    >
                      <Controller
                        control={control}
                        name="documentCurrency"
                        as={
                          <Select>
                            {Object.entries(CURRENCY).map((c) => (
                              <Select.Option value={c[0]} key={c[0]}>
                                {c[1]}
                              </Select.Option>
                            ))}
                          </Select>
                        }
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={12}>
                <Row>
                  <Col xs={24} sm={12} lg={16}>
                    <FormItem
                      label="Sıklık"
                      required={true}
                      error={errors.frequencyVal}
                    >
                      <Controller
                        control={control}
                        name="frequencyVal"
                        as={
                          <InputNumber
                            disabled={
                              values.purpose?.rule?.code === RULES.RULE0007
                            }
                            style={{ width: "100%" }}
                            min={1}
                            step={1}
                          />
                        }
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} sm={12} lg={8}>
                    <FormItem
                      label="Sıklık Birimi"
                      required={true}
                      error={errors.frequencyUnit}
                    >
                      <FetchSelect
                        disabled={values.purpose?.rule?.code === RULES.RULE0007}
                        api="/masterdata/frequencyunits"
                        form={form}
                        field="frequencyUnit"
                        placeholder="Sıklık Birimi seçiniz"
                      />
                    </FormItem>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={12}>
                <FormItem label="Vade" error={errors.maturityVal}>
                  <Controller
                    control={control}
                    name="maturityVal"
                    as={
                      <InputNumber style={{ width: "100%" }} min={0} step={1} />
                    }
                  />
                </FormItem>
              </Col>
              <Col xs={24} sm={12}>
                <FormItem label="Artış Kuralı" error={errors.increaseType}>
                  <FetchSelect
                    api="/masterdata/increasetypes"
                    form={form}
                    field="increaseType"
                  />
                </FormItem>
              </Col>
              <Col xs={24}>
                <FormItem label="Açıklama" error={errors.description}>
                  <Controller
                    control={control}
                    name="description"
                    as={<Input.TextArea style={{ width: "100%" }} rows={5} />}
                  />
                </FormItem>
              </Col>
              <Col xs={24}>
                <Row justify="space-between" style={{ marginTop: 16 }}>
                  <Col>
                    <Button
                      loading={formState.isSubmitting}
                      style={{ width: 120 }}
                      type="primary"
                      htmlType="submit"
                    >
                      {selectedIndex !== null ? "Güncelle" : "Ekle"}
                    </Button>
                  </Col>
                </Row>
              </Col>
            </FormLayout>
          </form>
        ) : (
          <div
            style={{
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        )}
      </Drawer>
    </>
  );
}
